/**
 * [재고 조정] 창 — 재고 상세(제품 하나)에서 컨디션 하나의 수를 맞춘다(현민 2026-11-03 · VOCABULARY "재고 조정").
 * 빼기 N장(사유 넷) · 넣기 N장(실사 발견) · 컨디션 변경 N장. 시리얼이 없어 장은 서버가 고른다 — 사람은 수와 로트만 적는다.
 * 빼기 · 컨디션 변경은 로트(구매 줄)를 고른다 — 안 고르면 어느 로트가 빠질지 모른다(현민 2026-11-04 A).
 * 그리기만 — 상태는 StockDetailView 의 this.data.adjust.
 */
import type { EStockAdjustKind, ERetiredReason, StockAdjustInput, StockLineView } from '../api/client';
import { E_RETIRED_REASONS, E_STOCK_ADJUST_KINDS } from '../api/client';
import { CONDITION_OPTIONS } from '../lib/condition';
import { escapeHtml } from '../lib/cells';
import { costTextOf } from '../lib/money';
import { RETIRED_REASON_WORD } from './retire-dialog';
import { ORIGIN_WORD } from './unit-table';

/** 창의 초안 — 수량은 칸 글 그대로(숫자 판정은 adjustInputOrNullOf). null 이면 창이 닫혀 있다 */
export type AdjustDraft = {
  kind: EStockAdjustKind;
  condition: string;          // 고칠 장의 지금 컨디션 · 넣기는 새 장의 컨디션
  quantity: string;
  reason: ERetiredReason;     // 빼기만 쓴다
  to_condition: string;       // 컨디션 변경만 쓴다
  line_key: string;           // 빼기 · 컨디션 변경의 로트(구매 줄) 열쇠. '' = 아직 안 고름
  note: string;
};

export const ADJUST_KIND_WORD: Record<EStockAdjustKind, string> = { remove: '빼기', add: '넣기 (실사 발견)', regrade: '컨디션 변경' };
const ADJUST_KIND_NOTE: Record<EStockAdjustKind, string> = {
  remove: '그 로트의 검수 중 · 가용 장에서 N장을 사용 불가로 뺍니다 — 홀딩 · 판매에 묶인 장은 안 고릅니다.',
  add: '그 컨디션으로 새 장 N장을 만듭니다(가용 · 출처 "실사 발견" · 원가 없음). 기다리는 판매가 있으면 바로 묶입니다.',
  regrade: '그 로트의 검수 중 · 가용 장 N장의 컨디션을 바꿉니다 — 원가 · 홀딩은 그대로.',
};

/** 한 번에 고칠 수 있는 장 수 — 서버 /stock-adjustments 의 quantity 상한(Inventory.Routes ADJUST_BASE_SHAPE)과 같다 */
export const ADJUST_MAX_QUANTITY = 500;

/** 수량 칸 옆 한 줄 — 상한을 넘으면 그 말, 아니면 '' */
export function adjustQuantityHintOf(draft: AdjustDraft): string {
  return Number(draft.quantity) > ADJUST_MAX_QUANTITY ? `한 번에 ${ADJUST_MAX_QUANTITY}장까지` : '';
}

/** 처음 초안 — 컨디션은 페이지에서 고른 것, 로트는 재고 목록에서 누른 줄(preferredLineKey, '' = 없음). 그 컨디션의 로트가 하나뿐이면 그것 */
export function newAdjustDraft(condition: string, lots: readonly StockLineView[], preferredLineKey: string): AdjustDraft {
  return { kind: 'remove', condition, quantity: '1', reason: 'lost', to_condition: '', line_key: defaultLotKeyOf(adjustLotsOf(lots, condition), preferredLineKey), note: '' };
}

/** 고를 수 있는 로트 — 그 컨디션의 구매 줄 중 아직 우리 손에 있는 장(사용 중 + 사용 가능)이 있는 것 */
export function adjustLotsOf(lots: readonly StockLineView[], condition: string): StockLineView[] {
  return lots.filter((lot) => lot.condition === condition && lot.in_use_count + lot.usable_count > 0);
}

/** 먼저 골라 둘 로트 — 바라는 열쇠가 후보에 있으면 그것, 후보가 하나뿐이면 그것, 아니면 ''(사람이 고른다) */
export function defaultLotKeyOf(lots: readonly StockLineView[], preferredLineKey: string): string {
  if (lots.some((lot) => lot.line_key === preferredLineKey)) return preferredLineKey;
  return lots.length === 1 ? lots[0].line_key : '';
}

/** 로트 한 줄의 글 — "2026-10-13 · BEIYI · 8,400 원 · 사용 가능 89" · "구매 기록 없음(옛 재고) · — · — · 사용 가능 2" */
function lotWordOf(lot: StockLineView): string {
  const date = lot.purchased_date === null ? `구매 기록 없음(${ORIGIN_WORD[lot.origin]})` : lot.purchased_date;   // 기록 없는 로트끼리는 출처로 갈린다(옛 재고 · 실사 발견)
  const supplier = lot.supplier_name === null ? '—' : lot.supplier_name;
  return `${date} · ${supplier} · ${costTextOf(lot.unit_price, lot.cost_currency)} · 사용 가능 ${lot.usable_count}`;
}

/** 로트 칸 — 빼기 · 컨디션 변경만. 후보 없음은 글, 하나는 글, 둘 이상은 셀렉트(이벤트 adjust-lot) */
function renderLotField(draft: AdjustDraft, lots: readonly StockLineView[]): string {
  if (draft.kind === 'add' || draft.condition === '') return '';
  const candidates = adjustLotsOf(lots, draft.condition);
  if (candidates.length === 0) return '<p class="pv-adjust-lot is-empty">이 컨디션에 남은 로트가 없습니다</p>';
  if (candidates.length === 1) return `<p class="pv-adjust-lot">로트 — ${escapeHtml(lotWordOf(candidates[0]))}</p>`;
  const options = ['', ...candidates.map((lot) => lot.line_key)].map((key) => {
    const lot = candidates.find((candidate) => candidate.line_key === key);
    const word = lot === undefined ? '로트 고르기' : lotWordOf(lot);
    return `<option value="${escapeHtml(key)}" ${key === draft.line_key ? 'selected' : ''}>${escapeHtml(word)}</option>`;
  }).join('');
  return `<select class="pc-select pv-adjust-lot-select" data-action="adjust-lot" aria-label="로트">${options}</select>`;
}

/** 컨디션 후보 — 이 제품에 있는 컨디션 먼저, 그 뒤 표준 목록(겹치면 한 번) */
export function adjustConditionsOf(productConditions: readonly string[]): string[] {
  return [...new Set([...productConditions, ...CONDITION_OPTIONS])];
}

/** 보낼 몸 — 덜 찼으면 null([조정] 잠김): 컨디션 · 수량(1 ~ 500 정수) · 빼기 · 변경이면 로트 · 빼기 기타면 글 · 변경이면 다른 컨디션 */
export function adjustInputOrNullOf(draft: AdjustDraft, productId: string): StockAdjustInput | null {
  const quantity = Number(draft.quantity);
  const note = draft.note.trim() === '' ? null : draft.note.trim();
  if (draft.condition === '' || !Number.isInteger(quantity) || quantity < 1 || quantity > ADJUST_MAX_QUANTITY) return null;
  const base = { hclt_product_id: productId, condition: draft.condition, quantity, note };
  switch (draft.kind) {
    case 'remove': return (draft.reason === 'etc' && note === null) || draft.line_key === '' ? null : { ...base, kind: 'remove', retired_reason: draft.reason, line_key: draft.line_key };
    case 'add': return { ...base, kind: 'add' };
    case 'regrade': return draft.to_condition === '' || draft.to_condition === draft.condition || draft.line_key === '' ? null : { ...base, kind: 'regrade', to_condition: draft.to_condition, line_key: draft.line_key };
    default: throw new Error(`unhandled switch case: ${draft.kind satisfies never}`);
  }
}

/** [재고 조정] 창. 이벤트: adjust-kind · adjust-condition · adjust-lot · adjust-reason · adjust-to-condition(change) · adjust-quantity · adjust-note(input) · adjust-cancel · adjust-confirm(click).
 *  lots = 재고 상세가 받은 입고 줄(이 제품의 구매 줄 전부) */
export function renderAdjustDialog(draft: AdjustDraft, conditions: readonly string[], lots: readonly StockLineView[], productId: string, bActing: boolean): string {
  const kinds = E_STOCK_ADJUST_KINDS.map((kind) => `
    <label class="pv-retire-reason"><input type="radio" name="adjust-kind" data-action="adjust-kind" value="${kind}" ${draft.kind === kind ? 'checked' : ''}> ${ADJUST_KIND_WORD[kind]}</label>`).join('');
  const conditionOptions = (current: string): string => ['', ...conditions].map((c) => `<option value="${escapeHtml(c)}" ${c === current ? 'selected' : ''}>${c === '' ? '컨디션 고르기' : escapeHtml(c)}</option>`).join('');
  const reasons = E_RETIRED_REASONS.map((reason) => `
    <label class="pv-retire-reason"><input type="radio" name="adjust-reason" data-action="adjust-reason" value="${reason}" ${draft.reason === reason ? 'checked' : ''}> ${RETIRED_REASON_WORD[reason]}</label>`).join('');
  const bNoteRequired = draft.kind === 'remove' && draft.reason === 'etc';
  const bReady = adjustInputOrNullOf(draft, productId) !== null;
  return `
    <div class="mg-wrap" role="dialog" aria-label="재고 조정">
      <div class="mg-overlay" data-action="adjust-cancel"></div>
      <div class="mg-dialog pv-hold-dialog">
        <h3 class="mg-title">재고 조정</h3>
        <div class="pv-retire-reasons">${kinds}</div>
        <p class="mg-note">${ADJUST_KIND_NOTE[draft.kind]}</p>
        <div class="pv-adjust-row">
          <select class="pc-select" data-action="adjust-condition" aria-label="컨디션">${conditionOptions(draft.condition)}</select>
          ${draft.kind === 'regrade' ? `<span>→</span><select class="pc-select" data-action="adjust-to-condition" aria-label="바꿀 컨디션">${conditionOptions(draft.to_condition)}</select>` : ''}
          <input type="number" class="pv-adjust-qty" data-action="adjust-quantity" min="1" max="${ADJUST_MAX_QUANTITY}" step="1" value="${escapeHtml(draft.quantity)}" aria-label="수량"> 장
          <span class="pv-adjust-hint">${adjustQuantityHintOf(draft)}</span>
        </div>
        ${renderLotField(draft, lots)}
        ${draft.kind === 'remove' ? `<div class="pv-retire-reasons">${reasons}</div>` : ''}
        <input type="text" class="pv-hold-note" data-action="adjust-note" placeholder="${bNoteRequired ? '기타 — 무슨 일인지 한 줄(필수)' : '덧붙일 말 한 줄(선택) — 이력에 남습니다'}" value="${escapeHtml(draft.note)}">
        <div class="mg-buttons">
          <button type="button" class="btn-ghost" data-action="adjust-cancel">취소</button>
          <button type="button" class="btn-primary" data-action="adjust-confirm" ${!bReady || bActing ? 'disabled' : ''}>${bActing ? '보내는 중…' : '조정'}</button>
        </div>
      </div>
    </div>`;
}
